import { ArrowUpRight, ArrowDownRight, Star, Check } from 'lucide-react';
import type { NumericalRow } from '../lib/types';
import { TableOrCards, type Column } from './TableOrCards';
import { InfoPopover } from './InfoPopover';
import styles from './NumericalTable.module.css';

const dirIcon = {
  up: ArrowUpRight,
  down: ArrowDownRight,
  star: Star,
};
const dirColor: Record<NumericalRow['direction'], string> = {
  up: 'var(--tia-positive)',
  down: 'var(--tia-negative)',
  star: 'var(--tia-info)',
};

const beatColor: Record<NonNullable<NumericalRow['beat']>, string> = {
  beat: 'var(--tia-positive)',
  miss: 'var(--tia-negative)',
  'in-line': 'var(--fg-muted)',
};

function ConsensusCell({ row }: { row: NumericalRow }) {
  if (!row.consensus) return <span className={styles.notAligned}>—</span>;
  const delta =
    row.numericValue != null && row.consensus.numericValue != null
      ? row.numericValue - row.consensus.numericValue
      : undefined;
  const deltaText =
    delta != null
      ? `${delta > 0 ? '+' : ''}${delta.toFixed(delta > 100 ? 0 : 1)}`
      : '';
  return (
    <span className={styles.consensusCell}>
      <span className={[styles.consensusValue, 'mono'].join(' ')}>{row.consensus.value}</span>
      {row.beat && (
        <span className={styles.beatPill} style={{ color: beatColor[row.beat] }}>
          {row.beat.toUpperCase()} {deltaText && <span className="mono">{deltaText}</span>}
        </span>
      )}
      <span className={styles.consensusSource}>{row.consensus.source}</span>
    </span>
  );
}

export function NumericalTable({ rows, className }: { rows: NumericalRow[]; className?: string }) {
  const columns: Column<NumericalRow>[] = [
    {
      key: 'metric',
      header: 'Metric',
      sticky: true,
      render: (r) => (
        <span className={styles.metricCell}>
          <span className={styles.metric}>{r.metric}</span>
          {r.nonGaap && (
            <span className={styles.nonGaapTag} title="Non-GAAP / adjusted figure">non-GAAP</span>
          )}
        </span>
      ),
    },
    { key: 'value', header: 'Value', align: 'right', render: (r) => <span className={[styles.value, 'mono'].join(' ')}>{r.value}</span> },
    { key: 'consensus', header: 'Consensus', render: (r) => <ConsensusCell row={r} /> },
    { key: 'period', header: 'Period', render: (r) => <span className={[styles.period, 'mono'].join(' ')}>{r.period}</span> },
    {
      key: 'direction',
      header: 'Direction',
      align: 'center',
      render: (r) => {
        const Icon = dirIcon[r.direction];
        return <Icon size={18} strokeWidth={1.5} color={dirColor[r.direction]} aria-label={r.direction} />;
      },
    },
    {
      key: 'framing',
      header: 'Qualitative Framing',
      render: (r) => <em className={styles.framing}>{r.framing}</em>,
    },
    {
      key: 'aligned',
      header: 'Aligned',
      align: 'center',
      render: (r) =>
        r.aligned ? (
          <span className={styles.alignedWrap} title="Qualitative framing matches numerical direction.">
            <Check size={16} strokeWidth={2} color="var(--tia-navy)" aria-hidden="true" />
            <span className="sr-only">Aligned</span>
          </span>
        ) : (
          <span className={styles.notAligned}>—</span>
        ),
    },
  ];

  return (
    <div className={className}>
      <div className={styles.headerRow}>
        <span className={styles.eyebrow}>Numerical Extraction</span>
        <InfoPopover
          content={
            <span>
              Each row pairs a quantitative claim from the source with the qualitative framing used to introduce it.
              The Aligned check is true when the framing matches the direction.
            </span>
          }
          label="What is numerical alignment?"
        />
      </div>
      <TableOrCards
        ariaLabel="Numerical extraction"
        columns={columns}
        rows={rows}
        rowKey={(r) => `${r.metric}-${r.period}`}
        cardTitle={(r) => <span className={styles.cardTitle}>{r.metric}</span>}
        cardMeta={(r) => <span className={[styles.value, 'mono'].join(' ')}>{r.value}</span>}
        cardBody={(r) => <em className={styles.framing}>{r.framing}</em>}
      />
    </div>
  );
}
